/**
 * Same hostname rules as WebApp `hostOrigins.ts` — keep in sync when changing staging/production mapping.
 */

const ARR_COM = 'arrangelly.com';

function isArrangellyCom(hostname: string): boolean {
  return hostname === ARR_COM || hostname.endsWith(`.${ARR_COM}`);
}

function envPrefixFromCompoundFirstLabel(first: string): string | null {
  const lower = first.toLowerCase();
  if (lower.endsWith('-dashboard')) {
    return lower.slice(0, -'-dashboard'.length);
  }
  if (lower.endsWith('-api')) {
    return lower.slice(0, -'-api'.length);
  }
  return null;
}

export function getMarketingSiteOrigin(): string {
  const override = import.meta.env.VITE_MARKETING_ORIGIN?.trim();
  if (override) {
    return override.replace(/\/$/, '');
  }

  const { protocol, hostname } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//arrangelly.local`;
  }

  if (/^dashboard\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//${ARR_COM}`;
  }

  if (hostname.startsWith('dashboard.') && isArrangellyCom(hostname)) {
    return `${protocol}//${hostname.slice('dashboard.'.length)}`;
  }

  const parts = hostname.split('.');
  if (parts.length === 3 && parts[1] === 'arrangelly' && parts[2] === 'com') {
    const envFromCompound = envPrefixFromCompoundFirstLabel(parts[0]);
    if (envFromCompound) {
      return `${protocol}//${envFromCompound}.${ARR_COM}`;
    }
  }

  return `${protocol}//${hostname}`;
}

/** Public teacher portfolio on the marketing site (never the dashboard host). */
export function getPublicPortfolioUrl(slug: string): string {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) {
    return '';
  }
  return `${getMarketingSiteOrigin()}/${encodeURIComponent(normalized)}`;
}

export function getDashboardOrigin(): string {
  const override = import.meta.env.VITE_DASHBOARD_ORIGIN?.trim();
  if (override) {
    return override.replace(/\/$/, '');
  }

  const { protocol, hostname } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//dashboard.arrangelly.local`;
  }
  if (hostname.endsWith('.arrangelly.local') || hostname === 'arrangelly.local') {
    return `${protocol}//dashboard.arrangelly.local`;
  }
  if (!hostname.endsWith(`.${ARR_COM}`) && hostname !== ARR_COM) {
    return `${protocol}//dashboard.${ARR_COM}`;
  }

  if (hostname === ARR_COM || /^www\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//dashboard.${ARR_COM}`;
  }

  if (hostname.startsWith('dashboard.') && isArrangellyCom(hostname)) {
    return `${protocol}//${hostname}`;
  }

  const parts = hostname.split('.');
  if (parts.length === 3 && parts[1] === 'arrangelly' && parts[2] === 'com') {
    const first = parts[0].toLowerCase();

    if (first.endsWith('-dashboard')) {
      return `${protocol}//${hostname}`;
    }
    if (first.endsWith('-api')) {
      const env = envPrefixFromCompoundFirstLabel(parts[0])!;
      return `${protocol}//${env}-dashboard.${ARR_COM}`;
    }
    if (first !== 'www' && first !== 'api' && first !== 'dashboard') {
      return `${protocol}//${first}-dashboard.${ARR_COM}`;
    }
    if (first === 'api') {
      return `${protocol}//dashboard.${ARR_COM}`;
    }
  }

  return `${protocol}//dashboard.${ARR_COM}`;
}

export function getDerivedApiBaseUrl(): string {
  const env = import.meta.env.VITE_API_BASE_URL?.trim();
  if (env) {
    return env;
  }

  const { protocol, hostname } = window.location;

  if (hostname.endsWith('.arrangelly.local') || hostname === 'arrangelly.local') {
    return `${protocol}//api.arrangelly.local`;
  }

  if (!isArrangellyCom(hostname)) {
    return '/api';
  }

  if (hostname === ARR_COM || /^www\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//api.${ARR_COM}`;
  }

  if (/^api\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//${hostname}`;
  }

  if (/^dashboard\.arrangelly\.com$/i.test(hostname)) {
    return `${protocol}//api.${ARR_COM}`;
  }

  if (hostname.startsWith('dashboard.') && isArrangellyCom(hostname)) {
    const rest = hostname.slice('dashboard.'.length);
    if (rest === ARR_COM) {
      return `${protocol}//api.${ARR_COM}`;
    }
    if (rest.endsWith(`.${ARR_COM}`)) {
      const sub = rest.slice(0, -(`.${ARR_COM}`).length);
      if (sub && !sub.includes('.')) {
        return `${protocol}//${sub}-api.${ARR_COM}`;
      }
    }
    return `${protocol}//api.${rest}`;
  }

  const parts = hostname.split('.');
  if (parts.length === 3 && parts[1] === 'arrangelly' && parts[2] === 'com') {
    const first = parts[0].toLowerCase();
    if (first.endsWith('-dashboard')) {
      const env = envPrefixFromCompoundFirstLabel(parts[0])!;
      return `${protocol}//${env}-api.${ARR_COM}`;
    }
    if (first.endsWith('-api')) {
      return `${protocol}//${hostname}`;
    }
    if (first !== 'www' && first !== 'api' && first !== 'dashboard') {
      return `${protocol}//${first}-api.${ARR_COM}`;
    }
  }

  return `${protocol}//api.${ARR_COM}`;
}
